import type { DiscoveredService } from '#~/agent/discovery/utils.js';
import { useAgents } from '#~/agent/hooks.js';
import { ElectronAPI } from '#~/electron/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import type { Agent } from '@studio/models/agent';
import { useLatestFn, useUpdateEffect } from '@studio/react-utils/hooks';
import { noop } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import type { DiscoveryOptions } from './commands/index.js';
import { discover, KickFeedback, Timeout } from './commands/index.js';
import type { UseDiscovery } from './types.js';
import { broker } from '#~/agent/broker.js';
import { logger } from '#~/logger/index.js';
import { aggregateDiscoveryFailureCopy, type DiscoveryFlightState } from './discovery-outcome.js';
import { collectDiscoveryAgentIds, getDiscoveryFlight, requestAgentDiscovery } from './request.js';
import { traceCameraAdd } from '../add-trace.js';

const isAgentOffline = (status: Agent['status']): boolean =>
  status === 'offline' || status === 'offline-embedded';

// Offline agents and embedded cameras cannot answer discovery kicks.
// The agents store carries both fields; broker proxies lack status.
const isExcludedFromDiscovery = (agent: {
  status: Agent['status'];
  embedded?: Agent['embedded'];
}): boolean => isAgentOffline(agent.status) || agent.embedded === true;

// D-B6: discovery targets are every broker proxy minus the excluded agents.
const discoverableAgentIds = (
  ids: readonly Agent['id'][],
  excluded: ReadonlySet<Agent['id']>
): Agent['id'][] => ids.filter((id) => !excluded.has(id));

/**
 * Declarative discovery while Add Source is mounted: set `discovering` on
 * every proxy, re-arm the legacy `command.discovery` kick so old encoders
 * keep scanning, and expose Search Again as an immediate kick.
 *
 * Per rule: wire-contract-changes-must-not-break-old-firmware (ai.knowledge/global)
 * — the 15 s command re-arm is the old-encoder path.
 * Per rule: settle-every-terminal-branch (ai.knowledge/global) — unmount
 * always clears the interval, the spinner timer, and `discovering`.
 *
 * Fingerprinting always runs; discovery does not wait for feature-flag hydration.
 */
export const useDiscovery = (_agentId: Agent['id'], options?: DiscoveryOptions): UseDiscovery => {
  const [isRunning, setIsRunning] = useState(false);
  const [states, setStates] = useState<Map<Agent['id'], DiscoveryFlightState>>(new Map());
  const agentIdRef = useRef(_agentId);
  const optionsRef = useRef(options);
  const sendKickRef = useRef<(withSpinner: boolean) => void>(() => undefined);
  const ceilingsRef = useRef<Map<Agent['id'], number>>(new Map());
  const spinnerActiveRef = useRef(false);

  agentIdRef.current = _agentId;
  optionsRef.current = options;

  const hasLiveAgent = useAgents((agents) => agents.some((agent) => !isAgentOffline(agent.status)));

  // Keep current exclusions available to the mount effect without re-subscribing.
  const excludedIds = useAgents(
    (agents) => new Set(agents.filter(isExcludedFromDiscovery).map((agent) => agent.id)),
    // Membership equality: a status/name write that leaves the excluded set
    // unchanged must not wake this consumer or rebuild the discovery closures.
    (a, b) => a.size === b.size && [...a].every((id) => b.has(id))
  );
  const excludedIdsRef = useRef(excludedIds);
  excludedIdsRef.current = excludedIds;

  useEffect(() => {
    let cancelled = false;
    let runningTimer: number | undefined;

    const applyDiscovering = (on: boolean): void => {
      for (const proxy of broker) {
        traceCameraAdd('discovery.toggle', { agentId: proxy.id, enabled: on });
        void proxy.setDiscovering(on);
      }
    };

    const publish = (): void => {
      const current = new Map<Agent['id'], DiscoveryFlightState>();
      // Exclude stale flights left over from when an agent was online.
      for (const id of discoverableAgentIds(
        collectDiscoveryAgentIds(agentIdRef.current, broker),
        excludedIdsRef.current
      )) {
        const flight = getDiscoveryFlight(id);
        if (flight) current.set(id, flight.state);
      }
      setStates(current);
    };

    const clearCeiling = (id: Agent['id']): void => {
      const timer = ceilingsRef.current.get(id);

      if (timer !== undefined) {
        window.clearTimeout(timer);
        ceilingsRef.current.delete(id);
      }
    };

    const armCeiling = (id: Agent['id']): void => {
      const flight = getDiscoveryFlight(id);
      if (!flight?.promise || ceilingsRef.current.has(id) || flight.deadline <= Date.now()) return;
      ceilingsRef.current.set(
        id,
        window.setTimeout(() => {
          ceilingsRef.current.delete(id);
          if (!cancelled) {
            publish();
            clearRunningIfIdle();
          }
        }, flight.deadline - Date.now())
      );
    };

    const clearRunningIfIdle = (): void => {
      if (
        !cancelled &&
        !discoverableAgentIds(
          collectDiscoveryAgentIds(agentIdRef.current, broker),
          excludedIdsRef.current
        ).some((id) => getDiscoveryFlight(id)?.state.kind === 'pending') &&
        !spinnerActiveRef.current
      ) {
        setIsRunning(false);
      }
    };

    const sendKick = (withSpinner: boolean): void => {
      if (cancelled) {
        return;
      }

      const timeout = optionsRef.current?.timeout ?? Timeout;
      // D-B6: never kick an excluded (offline/embedded) agent.
      const agentIds = discoverableAgentIds(
        collectDiscoveryAgentIds(agentIdRef.current, broker),
        excludedIdsRef.current
      );

      if (withSpinner) {
        setIsRunning(true);
        spinnerActiveRef.current = true;
        if (runningTimer !== undefined) {
          window.clearTimeout(runningTimer);
        }
        // Spinner is kick feedback, not scan lifetime.
        runningTimer = window.setTimeout(() => {
          spinnerActiveRef.current = false;
          runningTimer = undefined;
          clearRunningIfIdle();
        }, KickFeedback);
      }

      if (agentIds.length === 0) {
        return;
      }

      traceCameraAdd('fingerprint.discovery-request', { agentIds, extendedDiscovery: true });
      for (const id of agentIds) {
        const pending = requestAgentDiscovery(id, { timeout, extendedDiscovery: true }, discover);
        armCeiling(id);
        void pending
          .then(() => {
            if (cancelled) {
              return;
            }
            clearCeiling(id);
            publish();
            clearRunningIfIdle();
          })
          .catch((err: unknown) => {
            // See knowledge: use-canonical-logger-not-console (ai.knowledge/studio.client).
            logger.error('device discovery request rejected', err);
          });
      }
      publish();
    };

    sendKickRef.current = sendKick;
    applyDiscovering(true);
    // Proxies created after mount (a bridge linking while Add Source is open) must also scan.
    const unsubscribe = broker.subscribe(() => {
      applyDiscovering(true);
    });
    sendKick(true);
    const intervalId = window.setInterval(() => sendKick(false), Timeout);

    return () => {
      cancelled = true;
      sendKickRef.current = () => undefined;
      window.clearInterval(intervalId);
      if (runningTimer !== undefined) {
        window.clearTimeout(runningTimer);
      }
      for (const timer of ceilingsRef.current.values()) {
        window.clearTimeout(timer);
      }
      ceilingsRef.current.clear();
      spinnerActiveRef.current = false;
      unsubscribe();
      applyDiscovering(false);
      setIsRunning(false);
    };
  }, []);

  const handleDiscovery = useLatestFn(() => {
    sendKickRef.current(true);
  });

  return {
    discover: handleDiscovery,
    isRunning,
    isActive: hasLiveAgent,
    failureCopy: aggregateDiscoveryFailureCopy(states, _agentId)
  };
};

/**
 * Hook that provides imperative access to Iris mDNS services discovery.
 *
 * @param options - Discovery options.
 * @returns Iris mDNS services discovery hook.
 */
export const useDiscoveredIrisServices = (options?: DiscoveryOptions) => {
  const [services, setServices] = useState<DiscoveredService[] | null>(null);
  const [timeoutId, setTimeoutId] = useState<number>();

  useUpdateEffect(() => {
    if (!timeoutId) {
      return noop;
    }

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [timeoutId]);

  const handleDiscoveryOfIrisServices = useLatestFn(() => {
    if (!isElectronApp()) return;

    // Is there already a discovery in progress?
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }

    const timeout = options?.timeout ?? Timeout;

    // When we begin discovery, start the timer.
    setTimeoutId(
      window.setTimeout(() => {
        // After the timeout period, reset the timer ID.
        setTimeoutId(undefined);
      }, timeout)
    );

    // Initial query for existing services
    const initializeServices = async () => {
      try {
        const discovered = await ElectronAPI.mDNS.getIrisDiscovered();
        // @debuggin
        // const discovered = await ElectronAPI.mDNS.getIrisTestDiscovered();
        const embeddedServices = discovered.filter(
          (service: DiscoveredService) => service.data?.bridgeType === 'e'
        );

        // Deduplicate initial services using the same logic
        const deduplicatedServices = embeddedServices.reduce(
          (acc: DiscoveredService[], service: DiscoveredService) => {
            const serviceKey = `${service.address}:${service.port}:${service.data?.machineId || service.name}`;
            const exists = acc.find((s) => {
              const existingKey = `${s.address}:${s.port}:${s.data?.machineId || s.name}`;
              return existingKey === serviceKey;
            });

            if (!exists) {
              acc.push(service);
            }
            return acc;
          },
          []
        );

        setServices(deduplicatedServices || []);
      } catch (error) {
        console.error('Failed to initialize Iris mDNS services', error);
        setServices([]);
      }
    };

    initializeServices();

    // Set up listener for new Iris services
    return ElectronAPI.mDNS.onIrisServiceDiscovered((service) => {
      setServices((prev) => {
        if (!prev) return [service];

        if (service.data?.bridgeType !== 'e') return prev;

        // Create a more robust unique identifier combining multiple properties
        const serviceKey = `${service.address}:${service.port}:${service.data?.machineId || service.name}`;
        const existing = prev.find((s) => {
          const existingKey = `${s.address}:${s.port}:${s.data?.machineId || s.name}`;
          return existingKey === serviceKey;
        });

        // if it exists, update it
        if (existing) {
          return prev.map((s) => {
            const existingKey = `${s.address}:${s.port}:${s.data?.machineId || s.name}`;
            if (existingKey === serviceKey) {
              return { ...s, ...service };
            }
            return s;
          });
        }

        // if it doesn't exist, add it
        return [...prev, service];
      });
    });
  });

  return {
    discover: handleDiscoveryOfIrisServices,
    services,
    isRunning: !!timeoutId
  };
};
